import React from 'react';
import './style.less';
import { Icon, IconName } from 'shared/components/Icon';
import { MediaDevices } from 'sb-lib/media-devices';
import { Select } from 'antd';
import { Button } from 'shared/components/Button';
import clsx from 'clsx';

export enum SettingItemType {
  Cam = 'cam',
  Mic = 'mic',
  Speaker = 'speaker',
}

interface Props {
  type: SettingItemType;
  value?: string;
  onChange?: (value: string) => void;
  options: MediaDevices;
  disabled?: boolean;
  isMuted?: boolean;
  onMute?: (isMuted: boolean) => void;
  optionOnly?: boolean;
  small?: boolean;
}

const SettingItemContent: Record<SettingItemType, { icon: IconName; title: string }> = {
  [SettingItemType.Cam]: {
    icon: 'videoCamera',
    title: 'Camera',
  },
  [SettingItemType.Mic]: {
    icon: 'micSalesfloorFill',
    title: 'Microphone',
  },
  [SettingItemType.Speaker]: {
    icon: 'audioSpeaker',
    title: 'Speakers',
  },
};

export const SalesfloorRoomSettingsItem: React.FC<Props> = ({
  type,
  value,
  onChange,
  options,
  disabled,
  onMute,
  isMuted,
  optionOnly,
  small,
}) => {
  return (
    <div className="SalesfloorRoomSettingsItem">
      <div
        className={clsx(
          'SalesfloorRoomSettingsItem__header',
          small && 'SalesfloorRoomSettingsItem__header--small',
        )}
      >
        <div className="SalesfloorRoomSettingsItem__header-inner">
          <Icon icon={SettingItemContent[type].icon} width={16} height={16} />
          {SettingItemContent[type].title}
        </div>
        {type === SettingItemType.Mic && (
          <div className="SalesfloorRoomSettingsItem__meter-container">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="SalesfloorRoomSettingsItem__meter-level" />
            ))}
          </div>
        )}
      </div>
      <div className="SalesfloorRoomSettingsItem__actions">
        {type === SettingItemType.Mic && !optionOnly && (
          <Button
            type="ghost"
            onClick={() => (onMute ? onMute(!isMuted) : null)}
            className={clsx(
              'SalesfloorRoomSettingsItem__button',
              isMuted && 'SalesfloorRoomSettingsItem__button--off',
            )}
            disabled={disabled}
          >
            <Icon
              icon={!isMuted ? 'micSalesfloorOutlineOn' : 'micSalesfloorOutlineOff'}
              width={20}
              height={20}
            />
            {!isMuted ? 'Mute' : 'Unmute'}
          </Button>
        )}
        {type === SettingItemType.Cam && !optionOnly && (
          <Button
            onClick={() => (onMute ? onMute(!isMuted) : null)}
            type="ghost"
            className={clsx(
              'SalesfloorRoomSettingsItem__button',
              isMuted && 'SalesfloorRoomSettingsItem__button--off',
            )}
            disabled={disabled}
          >
            <Icon
              icon={!isMuted ? 'cameraSalesfloorOn' : 'cameraSalesfloorOff2'}
              width={20}
              height={20}
            />
            {!isMuted ? 'Stop Video' : 'Start Video'}
          </Button>
        )}
        <Select
          className={clsx(
            'SalesfloorRoomSettingsItem__select',
            (type === SettingItemType.Speaker || optionOnly) &&
              'SalesfloorRoomSettingsItem__select--full',
            small && 'SalesfloorRoomSettingsItem__select--small',
          )}
          suffixIcon={<Icon icon="chevron" width={20} height={20} />}
          value={value}
          onChange={onChange}
          disabled={disabled || isMuted}
          dropdownClassName="SalesfloorRoomSettingsItem__dropdown"
        >
          {options.map(d => (
            <Select.Option value={d.id} key={d.id}>
              <span className="SalesfloorRoomSettingsItem__select-ellipsis">
                {d.label}
              </span>
            </Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );
};
